import React, { ReactNode } from 'react';
import RiskScorePill, { type AccountRisk } from './RiskScorePill';

export type WavvestAccountRow = {
  id: string;
  title: string;
  typeLabel?: string;
  risk?: AccountRisk | null;
  balance: string;
  logoSrc?: string;
  funding?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
  expanded: boolean;
  details?: ReactNode;
  onToggle: () => void;
};

export type WavvestAccountsTableProps = {
  total: string;
  subtitle?: string;
  showActions?: boolean;
  riskLoading?: boolean;
  rows: WavvestAccountRow[];
};

// Events from the funding chip, the action buttons, or anything portalled out
// of the row (e.g. the funding bank popover, rendered in document.body but
// still bubbling through React) must not toggle the row.
const isRowOwnEvent = (event: React.SyntheticEvent<HTMLElement>): boolean => {
  const target = event.target as Node;
  if (!event.currentTarget.contains(target)) {
    return false;
  }
  return !(
    target instanceof Element &&
    target.closest('.wavvest-accounts-funding, .wavvest-accounts-actions')
  );
};

const WavvestAccountsTable: React.FC<WavvestAccountsTableProps> = ({
  total,
  subtitle,
  showActions = false,
  riskLoading = false,
  rows,
}) => {
  if (rows.length === 0) {
    return null;
  }

  const riskCell = (row: WavvestAccountRow) =>
    riskLoading ? null : <RiskScorePill risk={row.risk} />;

  return (
    <section
      className={`wavvest-accounts${showActions ? '' : ' is-readonly'}`}
      aria-label="Wavvest accounts"
    >
      <header className="wavvest-accounts-header">
        <div>
          <h2>Wavvest accounts</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        <strong>{total}</strong>
      </header>
      <div className="wavvest-accounts-columns">
        <span>Account</span>
        <span>Risk</span>
        <span>Balance</span>
        <span>Funding source</span>
        {showActions ? <span>Actions</span> : null}
        <span className="wavvest-accounts-toggle-col" />
      </div>
      {rows.map((row) => {
        const toggleText = row.expanded ? 'See less' : 'See more';
        const toggleSign = row.expanded ? '-' : '+';
        return (
          <div key={row.id} className={row.expanded ? 'is-open' : undefined}>
            <div
              className={`wavvest-accounts-row${row.expanded ? ' is-open' : ''}`}
              role="button"
              tabIndex={0}
              aria-expanded={row.expanded}
              aria-label={`${row.expanded ? 'Hide' : 'Show'} details for ${row.title}`}
              onClick={(event) => {
                if (!isRowOwnEvent(event)) {
                  return;
                }
                row.onToggle();
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') {
                  return;
                }
                if (!isRowOwnEvent(event)) {
                  return;
                }
                event.preventDefault();
                row.onToggle();
              }}
            >
              <div className="wavvest-accounts-name">
                {row.logoSrc ? (
                  <img className="wavvest-account-mark" src={row.logoSrc} alt="" />
                ) : null}
                <span className="wavvest-accounts-copy">
                  <span className="wavvest-accounts-title">{row.title}</span>
                  {row.typeLabel ? (
                    <span className="wavvest-accounts-type">{row.typeLabel}</span>
                  ) : null}
                  {row.meta}
                  <span className="wavvest-accounts-risk-stacked">{riskCell(row)}</span>
                </span>
              </div>
              <span className="wavvest-accounts-risk">{riskCell(row)}</span>
              <span className="wavvest-accounts-balance">{row.balance}</span>
              <span className="wavvest-accounts-funding">{row.funding}</span>
              {showActions ? row.actions || <span /> : null}
              <span className="wavvest-accounts-toggle">
                <span className="wavvest-accounts-toggle-sign" aria-hidden="true">
                  {toggleSign}
                </span>
                <span>{toggleText}</span>
              </span>
            </div>
            {row.expanded && row.details ? (
              <div className="wavvest-account-panel">{row.details}</div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
};

export default WavvestAccountsTable;
